import { DATE_TIME_FORMATS } from "@clipboard-health/date-time/src/lib/internal/constants";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, FormControl, FormHelperText, Stack } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { DeprecatedGlobalAppV1Paths } from "@src/appV2/App/paths";
import { AppBarHeader, BackButtonLink, PageWithHeader, useToast } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { USER_EVENTS } from "@src/constants";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time. See https://www.notion.so/BP-Date-Time-2ef8643321f48032ad80e8a5b30aa278 for more information
import { differenceInDays, endOfDay, format, startOfDay, startOfYear } from "date-fns";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import { useSendProofOfEarningsEmail } from "./api/useSendProofOfEarningsEmail";

const proofOfEarningsSchema = z
  .object({
    start: z.date().transform((d) => startOfDay(d)),
    end: z.date().transform((d) => endOfDay(d)),
  })
  .superRefine((data, context) => {
    if (data.end < data.start) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date must be after start date",
        path: ["end"],
      });
    }

    const eod = endOfDay(new Date());

    if (data.end > eod) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        message: "End date cannot be after today",
        path: ["end"],
      });
    }
  });

function ProofOfEarningsPageWithHeader({ children }: { children: React.ReactNode }) {
  return (
    <PageWithHeader
      appBarHeader={
        <AppBarHeader
          title="Proof of Earnings"
          leftCta={<BackButtonLink defaultBackTo={DeprecatedGlobalAppV1Paths.PAYROLL} />}
        />
      }
    >
      {children}
    </PageWithHeader>
  );
}

export function ProofOfEarningsPage() {
  const { control, formState, watch, handleSubmit } = useForm({
    defaultValues: {
      start: startOfYear(new Date()),
      end: endOfDay(new Date()),
    },
    resolver: zodResolver(proofOfEarningsSchema),
  });
  const [start, end] = watch(["start", "end"]);
  const { showErrorToast, showInfoToast, showSuccessToast } = useToast();
  const sendProofOfEarningsEmail = useSendProofOfEarningsEmail();

  const worker = useDefinedWorker();

  const sendEmail = async (data: z.infer<typeof proofOfEarningsSchema>) => {
    showInfoToast("Sending email...");

    try {
      const result = await sendProofOfEarningsEmail.mutateAsync({
        start: data.start,
        end: data.end,
      });

      if (!result.data?.success) {
        throw new Error("Could not send email.");
      }

      showSuccessToast(`Proof of earnings sent to ${worker.email}`);
    } catch {
      showErrorToast("Could not send email. Please try again later.");
      return;
    }

    logEvent(USER_EVENTS.EMAIL_PAYSTUB, {
      startDate: format(data.start, "yyyy-MM-dd"),
      endDate: format(data.end, "yyyy-MM-dd"),
      dateRange: differenceInDays(data.end, data.start),
      userId: worker.userId,
    });
  };

  return (
    <ProofOfEarningsPageWithHeader>
      <form onSubmit={handleSubmit(sendEmail)}>
        <Stack spacing={2}>
          <Controller
            control={control}
            name="start"
            render={({ field }) => (
              <FormControl error={Boolean(formState.errors.start)}>
                <DatePicker
                  {...field}
                  disableFuture
                  label="Start Date"
                  format={DATE_TIME_FORMATS.date}
                  maxDate={end}
                />
                {formState.errors.start && (
                  <FormHelperText>{formState.errors.start.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
          <Controller
            control={control}
            name="end"
            render={({ field }) => (
              <FormControl error={Boolean(formState.errors.end)}>
                <DatePicker
                  {...field}
                  disableFuture
                  label="End Date"
                  format={DATE_TIME_FORMATS.date}
                  minDate={start}
                />
                {formState.errors.end && (
                  <FormHelperText>{formState.errors.end.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />
          <Button
            type="submit"
            size="small"
            variant="contained"
            color="primary"
            style={{ width: "100%" }}
            disabled={sendProofOfEarningsEmail.isLoading}
          >
            Email me my proof of earnings
          </Button>
        </Stack>
      </form>
    </ProofOfEarningsPageWithHeader>
  );
}
